import { useMemo } from 'react'
import Checkbox from '@mui/material/Checkbox'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import ListItemText from '@mui/material/ListItemText'
import MenuItem from '@mui/material/MenuItem'
import OutlinedInput from '@mui/material/OutlinedInput'
import Select, { type SelectChangeEvent } from '@mui/material/Select'
import { useTranslation } from 'react-i18next'

export type ChannelId = string | number

export type ChannelOption = {
  id: ChannelId
  name?: string
  type?: string
}

export const getSelectableChannels = (channelsList: ChannelOption[] = []) =>
  channelsList.filter((channel) => channel?.type !== 'person')

export const getEffectiveChannelIds = (
  selectedIds: ChannelId[] | undefined,
  channelsList: ChannelOption[] = []
) => {
  const total = getSelectableChannels(channelsList).length
  if (!selectedIds?.length || selectedIds.length === total) {
    return undefined
  }
  return selectedIds
}

type ChannelsFilterSelectProps = {
  channelsList?: ChannelOption[]
  value?: ChannelId[]
  onChange?: (channelIds: ChannelId[]) => void
  labelId?: string
}

const ChannelsFilterSelect = ({
  channelsList = [],
  value = [],
  onChange,
  labelId = 'channels-filter-label',
}: ChannelsFilterSelectProps) => {
  const { t } = useTranslation()
  const selectableChannels = useMemo(
    () => getSelectableChannels(channelsList),
    [channelsList]
  )

  const handleChange = (event: SelectChangeEvent<ChannelId[]>) => {
    const nextValue = event.target.value
    const channelIds = typeof nextValue === 'string' ? nextValue.split(',') : nextValue
    onChange?.(channelIds)
  }

  const renderValue = (selected: ChannelId[]) => {
    const total = selectableChannels.length
    if (!selected?.length || selected.length === total) {
      return t('all')
    }
    return `${selected.length}/${total}`
  }

  return (
    <FormControl
      size="small"
      sx={{
        minWidth: { xs: 120 },
      }}
    >
      <InputLabel id={labelId} shrink>
        {t('channels')}
      </InputLabel>
      <Select
        labelId={labelId}
        multiple
        displayEmpty
        value={value}
        onChange={handleChange}
        input={<OutlinedInput label={t('channels')} notched />}
        renderValue={renderValue}
      >
        {selectableChannels.map((channel) => (
          <MenuItem
            key={channel.id}
            value={channel.id}
          >
            <Checkbox checked={value.includes(channel.id) ?? false} />
            <ListItemText primary={channel?.name} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  )
}

export default ChannelsFilterSelect
